import { ApiView } from '@e3dc-react/shell/Elements/ApiView'
import { ApiTableColumn, FetchProps } from '@e3dc-react/shell/Elements/ApiView/ApiView'
import { TableCellPadding } from '@e3dc-react/shell/Elements/Table/types'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { UrlParams } from '@e3dc-react/shell/Types'
import { useMediaQuery, useTheme } from '@mui/material'
import React, { useCallback, useMemo, useState } from 'react'
import { Breakpoints } from 'src/Configuration/breakpoints'
import { InstallationStatusProvider } from 'src/Libs/installation-status'
import { useInstallationTableColumns } from 'src/Libs/installations/hooks/use-installation-table-columns'
import { getCollapsedTableColumn } from 'src/Libs/installations/utils/installation-table-columns'
import { makeStyles } from 'tss-react/mui'
import { getOwnerName, getProjectName } from '../installation.functions'
import { Installation } from '../models/installation.model'

const useStyles = makeStyles()(() => ({
  tableHeader: {
    whiteSpace: 'pre-line',
  },
  tableContainer: {
    display: 'flex',
    overflowX: 'hidden',
  },
  muiTableClassName: {
    maxWidth: '100%',
    tableLayout: 'fixed',
  },
  customFunctionalityClass: {
    textAlign: 'right',
  },
}))

export interface InstallationsApiViewProps {
  keyValue?: number
  fetchProps: FetchProps
  dataQaPrefix?: string
  containerClassName?: string
  customFunctionalities?: ((item: Installation) => React.ReactNode)[]
  customTableColumns?: ApiTableColumn<Installation>[]
  onSelect?: (installation?: Installation) => void
  onSelectOnlyInList?: boolean
  alternativeIcon?: React.ReactElement
  alternativeView?: React.ReactElement
  oneFilter?: {
    properties: (keyof Installation)[]
    propertiesWithoutWildCards?: string[]
    label: string
    filterQuery: string
    setFilterQuery: React.Dispatch<React.SetStateAction<string>>
    urlParams?: UrlParams
  }
  blankView?: JSX.Element
  showOptionsMenu?: boolean
  showStatus?: boolean
  showFullActionsMenu?: boolean
  actionButton?: React.ReactNode
  isPlannedView?: boolean
}

/**
 * This component is responsible of fetching installations and representing them in a table
 */
export const InstallationsApiView: React.FC<InstallationsApiViewProps> = ({
  keyValue,
  fetchProps,
  customFunctionalities,
  customTableColumns,
  dataQaPrefix,
  containerClassName = '',
  onSelect,
  oneFilter,
  alternativeIcon,
  alternativeView,
  blankView,
  showOptionsMenu = false,
  showStatus = false,
  showFullActionsMenu,
  actionButton,
  isPlannedView,
}) => {
  const { t } = useCustomTranslation()
  const theme = useTheme()
  const downSm = useMediaQuery(theme.breakpoints.down(Breakpoints.Mobile))
  const downLg = useMediaQuery(theme.breakpoints.down('lg'))
  const { classes } = useStyles()
  const { tableColumns } = useInstallationTableColumns({
    showOptionsMenu,
    showStatus,
    showFullActionsMenu,
    isPlannedView,
  })

  const [currentPageInstallationIds, setCurrentPageInstallationIds] = useState<number[]>([])

  const handleAfterFetch = useCallback((data?: Installation[]) => {
    if (data !== undefined) {
      setCurrentPageInstallationIds(data.map(i => i.id))
    }
  }, [])

  const handleCollapsedContentForInstallations = useMemo(() => {
    if (downLg) return getCollapsedTableColumn
  }, [downLg])

  return (
    <InstallationStatusProvider installationIds={currentPageInstallationIds}>
      <ApiView<Installation>
        key={keyValue}
        fetchProps={fetchProps}
        customFunctionalities={customFunctionalities}
        tableColumns={tableColumns}
        customTableColumns={customTableColumns}
        headerClassName={classes.tableHeader}
        containerClassName={[classes.tableContainer, containerClassName].join(' ')}
        muiTableClassName={classes.muiTableClassName}
        customFunctionalityClass={classes.customFunctionalityClass}
        hasColoredTableColumns
        isMobilePaginator={downSm}
        dataQaPrefix={dataQaPrefix}
        collapsibleContent={handleCollapsedContentForInstallations}
        collapsibleContentPadding={TableCellPadding.Checkbox}
        listProps={{
          primary: (installation: Installation) => getProjectName(installation, t),
          dataQa: (installation: Installation) => installation.projectName ?? installation.id.toString(),
          secondary: (installation: Installation) => getOwnerName(installation),
        }}
        onSelect={onSelect}
        oneFilter={oneFilter}
        alternativeView={alternativeView}
        alternativeIcon={alternativeIcon}
        onSelectOnlyInList
        bodyElevation={0}
        footerElevation={0}
        headerElevation={0}
        listElevation={0}
        scrollMarginTop={135}
        onlyTable
        onAfterFetch={handleAfterFetch}
        blankView={blankView}
        actionButton={actionButton}
      />
    </InstallationStatusProvider>
  )
}
